import React, { useEffect, useState, useMemo } from "react";
import { t as T } from "i18next";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { Settings, ExternalLink, Heart, MessageCircle, Lock, Play, Pencil, Eye, EyeOff, TrendingUp, Star, Send, QrCode, X, Sparkles, Plus, Image as ImageIcon, Briefcase, HelpCircle, Sliders, Zap, Handshake } from "lucide-react";
import "../styles/creator-page.css";
import CreatorEditModal from "../components/CreatorEditModal";
import CreatorAlbumModal from "../components/CreatorAlbumModal";
import CreatorInsightsTimeseries from "../components/CreatorInsightsTimeseries";
import AgeGateGuard from "../components/AgeGateGuard";
import AgeVerifyBadge from "../components/AgeVerifyBadge";
import AuthImage from "../components/AuthImage";
import PlatformIcon from "../components/PlatformIcon";
import SpinWheel from "../components/SpinWheel";
import CreatorTour from "../components/CreatorTour";
import { CREATOR_TOUR_STEPS } from "../components/tour/steps_creator";
import CountryFlag from "../components/CountryFlag";
import PostComposer from "../components/beta/PostComposer";
import PostFeedList from "../components/beta/PostFeedList";
import OpenRequestModal from "../components/collab/OpenRequestModal";

function themeStyle(t) {
  return {
    "--c-bg": t.bg, "--c-surface": t.surface, "--c-surface-2": t.surface_2,
    "--c-text": t.text, "--c-text-soft": t.text_soft, "--c-accent": t.accent,
    "--c-on-accent": t.on_accent, "--c-border": t.border,
    "--c-heading-font": `'${t.heading_font}', serif`,
    "--c-heading-style": t.heading_style,
    "--c-body-font": `'${t.body_font}', sans-serif`,
    "--c-btn-radius": t.button_radius,
  };
}

// Helper: spor klikk på enhver skapersidens-element
function trackElementClick(creatorId, targetType, targetId) {
  if (!creatorId || !targetId) return;
  api.post("/creator-element-click", { creator_id: creatorId, target_type: targetType, target_id: targetId }).catch(() => {});
}

function PromoCard({ promo, theme, creatorId, highlight }) {
  const cardRef = React.useRef(null);
  React.useEffect(() => {
    if (highlight && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlight]);
  const accentMap = {
    magenta:    "#F71868",
    gold:       "#D4A574",
    cognac:     "#D97757",
    sage:       "#7FA88E",
    eucalyptus: "#5FA59E",
    neon:       "#FF1A8C",
    aqua:       "#0066FF",
  };
  const ax = accentMap[promo.accent] || theme.accent;
  const inner = (
    <div
      ref={cardRef}
      className={`p-3 transition hover:translate-x-1 ${highlight ? "ring-2 ring-offset-2 ring-offset-transparent" : ""}`}
      style={{
        background: theme.surface,
        borderLeft: `3px solid ${ax}`,
        border: `1px solid ${theme.border}`,
        borderLeftWidth: 3,
        borderLeftColor: ax,
        ...(highlight ? { boxShadow: `0 0 0 2px ${ax}, 0 0 24px ${ax}55` } : {}),
      }}
      data-testid={highlight ? `promo-highlighted-${promo.id}` : undefined}
    >
      <div className="text-sm font-semibold" style={{ color: theme.text }}>{promo.title}</div>
      {promo.body && (
        <div className="text-xs mt-1" style={{ color: theme.text_soft, lineHeight: 1.5 }}>{promo.body}</div>
      )}
      {promo.link && (
        <div className="font-meta mt-2" style={{ color: ax }}>
          {promo.link_label || "Sjekk det ut"} →
        </div>
      )}
    </div>
  );
  return promo.link ? (
    <a href={promo.link} target="_blank" rel="noopener noreferrer"
      onClick={() => trackElementClick(creatorId, "promo", promo.id)}
      data-testid={`promo-${promo.id}`}>
      {inner}
    </a>
  ) : (
    <div
      onClick={() => trackElementClick(creatorId, "promo", promo.id)}
      data-testid={`promo-${promo.id}`}>{inner}</div>
  );
}

function AdCard({ ad, theme, creatorId }) {
  const isBoosted = ad.boosted_until && new Date(ad.boosted_until) > new Date();
  const firstImg = (ad.image_paths || [])[0];
  const imgSrc = firstImg
    ? `${process.env.REACT_APP_BACKEND_URL}/api/ads/images/raw?path=${encodeURIComponent(firstImg)}`
    : null;
  return (
    <Link
      to={`/annonser/se/${ad.id}`}
      onClick={() => trackElementClick(creatorId, "ad", ad.id)}
      data-testid={`ad-${ad.id}`}
      className="flex gap-2 p-2 transition hover:translate-x-1"
      style={{ background: theme.surface, border: `1px solid ${theme.border}` }}
    >
      <div className="w-12 h-12 shrink-0 overflow-hidden" style={{ background: theme.surface_2 }}>
        {imgSrc ? <img src={imgSrc} alt="" className="w-full h-full object-cover" /> : null}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs font-semibold truncate" style={{ color: theme.text }}>
          {isBoosted && <span className="mr-1" style={{ color: theme.accent }}>🚀</span>}
          {ad.title}
        </div>
        <div className="text-[10px] mt-0.5 inline-flex items-center gap-1.5" style={{ color: theme.text_soft, letterSpacing: "0.1em", textTransform: "uppercase" }}>
          {ad.country && <CountryFlag iso={ad.country} size="xs" />}
          <span>{ad.category || "—"}{ad.fylke ? ` · ${ad.fylke}` : ""}</span>
        </div>
      </div>
    </Link>
  );
}

function CompactReviewsSection({ username, theme, reviews, me, isOwner, onChanged }) {
  const { t: tr } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [writeOpen, setWriteOpen] = useState(false);

  const list = expanded ? reviews.results : reviews.results.slice(0, 5);
  const hasMore = reviews.results.length > 5;

  if (reviews.results.length === 0 && !(me && reviews.can_review)) return null;

  return (
    <div className="mt-8" data-testid="creator-reviews-compact">
      <div className="c-section-title flex items-center justify-between">
        <span>{tr("creator.reviews")}</span>
        {reviews.count > 0 && (
          <span className="text-xs" style={{ color: theme.text_soft }}>
            ★ {reviews.avg_stars.toFixed(1)} · {reviews.count}
          </span>
        )}
      </div>

      {/* Skriv-knapp – kun for ikke-eier som kan anmelde */}
      {!isOwner && me && reviews.can_review && !writeOpen && (
        <button
          onClick={() => setWriteOpen(true)}
          data-testid="open-review-form-btn"
          className="c-btn c-btn-outline w-full mb-3"
          style={{ justifyContent: "center" }}
        >
          {reviews.my_review ? "Endre anmeldelse" : tr("creator.write_review")}
        </button>
      )}

      {writeOpen && (
        <div className="mb-3">
          <InlineReviewForm
            username={username} theme={theme} myReview={reviews.my_review}
            onDone={() => { setWriteOpen(false); onChanged(); }}
            onCancel={() => setWriteOpen(false)}
          />
        </div>
      )}

      {list.length === 0 ? (
        <div className="c-card text-center text-xs" style={{ color: theme.text_soft }}>
          {tr("creator.no_reviews_yet")}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {list.map((r) => (
            <div key={r.id} className="c-card" style={{ padding: 12 }} data-testid={`review-compact-${r.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-7 h-7 overflow-hidden shrink-0" style={{ background: theme.surface_2 }}>
                  {r.reviewer?.avatar_url
                    ? <img src={r.reviewer.avatar_url} alt="" className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center text-[10px]" style={{ color: theme.text_soft }}>
                        {(r.reviewer?.username || "?").slice(0, 2).toUpperCase()}
                      </div>}
                </div>
                <div className="text-xs font-medium truncate flex-1 min-w-0" style={{ color: theme.text }}>
                  {r.reviewer?.username || "ukjent"}
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} className="h-3 w-3" fill={n <= r.stars ? theme.accent : "none"} style={{ color: theme.accent }} />
                  ))}
                </div>
              </div>
              {r.text && (
                <p className="text-xs" style={{ color: theme.text_soft, lineHeight: 1.5 }}>{r.text}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {hasMore && (
        <button
          onClick={() => setExpanded(!expanded)}
          data-testid="toggle-all-reviews-btn"
          className="block w-full text-center text-xs mt-3 underline"
          style={{ color: theme.accent }}
        >
          {expanded ? "Vis mindre" : `Les alle (${reviews.count})`}
        </button>
      )}
    </div>
  );
}

function InlineReviewForm({ username, theme, myReview, onDone, onCancel }) {
  const [stars, setStars] = useState(myReview?.stars || 0);
  const [text, setText] = useState(myReview?.text || "");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!stars) return;
    setSubmitting(true);
    try {
      await api.post(`/users/${username}/creator-reviews`, { stars, text });
      onDone();
    } catch (e) {
      const detail = e.response?.data?.detail;
      alert(typeof detail === "string" ? detail : T("ui.send_failed2"));
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Slette din anmeldelse?")) return;
    await api.delete(`/users/${username}/creator-reviews/me`);
    onDone();
  };

  return (
    <div className="c-card" style={{ padding: 12 }}>
      <div className="flex items-center gap-1 mb-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n} onClick={() => setStars(n)}
            data-testid={`review-star-${n}`}
            className="transition-transform hover:scale-110"
            style={{ color: n <= stars ? theme.accent : theme.text_soft }}
          >
            <Star className="h-5 w-5" fill={n <= stars ? theme.accent : "none"} />
          </button>
        ))}
      </div>
      <textarea
        value={text} onChange={(e) => setText(e.target.value)}
        placeholder={T("ui.review_ph")}
        maxLength={600} rows={3}
        className="w-full px-2 py-1.5 outline-none text-xs"
        style={{ background: theme.surface_2, color: theme.text, border: `1px solid ${theme.border}` }}
      />
      <div className="flex justify-end gap-2 mt-2">
        {myReview && (
          <button onClick={remove} className="text-xs underline" style={{ color: theme.text_soft }}>{T("ui.delete")}</button>
        )}
        <button onClick={onCancel} className="text-xs" style={{ color: theme.text_soft }}>{T("ui.cancel")}</button>
        <button onClick={submit} disabled={!stars || submitting}
          data-testid="review-submit-btn"
          className="c-btn c-btn-solid disabled:opacity-50" style={{ padding: "6px 12px", fontSize: 11 }}>
          {submitting ? "..." : (myReview ? "Oppdater" : "Send")}
        </button>
      </div>
    </div>
  );
}

function ReviewsSection({ username, theme, reviews, me, isOwner, onChanged }) {
  const { t: tr } = useTranslation();
  const [stars, setStars] = useState(reviews.my_review?.stars || 0);
  const [text, setText] = useState(reviews.my_review?.text || "");
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    setStars(reviews.my_review?.stars || 0);
    setText(reviews.my_review?.text || "");
  }, [reviews.my_review]);

  const submit = async () => {
    if (!stars) return;
    setSubmitting(true);
    try {
      await api.post(`/users/${username}/creator-reviews`, { stars, text });
      onChanged();
    } catch (e) {
      const detail = e.response?.data?.detail;
      alert(typeof detail === "string" ? detail : T("ui.review_failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Slette din anmeldelse?")) return;
    await api.delete(`/users/${username}/creator-reviews/me`);
    setStars(0); setText(""); onChanged();
  };

  return (
    <div className="max-w-5xl mx-auto mt-16 px-4" data-testid="creator-reviews">
      <div className="c-section-title flex items-center justify-between">
        <span>{T("ui.reviews")}</span>
        {reviews.count > 0 && (
          <span className="text-xs" style={{ color: theme.text_soft }}>
            ★ {reviews.avg_stars.toFixed(1)} · {reviews.count} {reviews.count === 1 ? "anmeldelse" : "anmeldelser"}
          </span>
        )}
      </div>

      {/* Skrive-form (kun for ikke-eier, må følge) */}
      {!isOwner && me && reviews.can_review && (
        <div className="c-card mb-6" data-testid="review-form">
          <div className="flex items-center gap-1 mb-3">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n} onClick={() => setStars(n)}
                data-testid={`review-star-${n}`}
                className="transition-transform hover:scale-110"
                style={{ color: n <= stars ? theme.accent : theme.text_soft }}
              >
                <Star className="h-7 w-7" fill={n <= stars ? theme.accent : "none"} />
              </button>
            ))}
            {reviews.my_review && (
              <button onClick={remove} className="ml-3 text-xs underline" style={{ color: theme.text_soft }}>
                Slett min anmeldelse
              </button>
            )}
          </div>
          <textarea
            value={text} onChange={(e) => setText(e.target.value)}
            placeholder={T("ui.review_ph")}
            maxLength={600}
            rows={3}
            className="w-full px-3 py-2 outline-none"
            style={{ background: theme.surface_2, color: theme.text, border: `1px solid ${theme.border}` }}
          />
          <div className="flex justify-end mt-3">
            <button
              onClick={submit} disabled={!stars || submitting}
              data-testid="review-submit-btn"
              className="c-btn c-btn-solid disabled:opacity-50"
            >
              {submitting ? tr("creator.sending") : reviews.my_review ? tr("creator.update") : tr("creator.publish_review")}
            </button>
          </div>
        </div>
      )}

      {!isOwner && me && !reviews.can_review && reviews.results.length === 0 && (
        <div className="c-card text-center text-sm" style={{ color: theme.text_soft }}>
          {tr("creator.must_follow_to_review")}
        </div>
      )}

      {/* Liste */}
      {reviews.results.length === 0 && !reviews.my_review ? (
        <div className="c-card text-center text-sm" style={{ color: theme.text_soft }}>
          {tr("creator.no_reviews_yet")}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {reviews.results.map((r) => (
            <div key={r.id} className="c-card" data-testid={`review-${r.id}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 overflow-hidden" style={{ background: theme.surface_2 }}>
                  {r.reviewer?.avatar_url
                    ? <img src={r.reviewer.avatar_url} alt="" className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center text-xs" style={{ color: theme.text_soft }}>
                        {(r.reviewer?.username || "?").slice(0, 2).toUpperCase()}
                      </div>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate" style={{ color: theme.text }}>
                    {r.reviewer?.username || "ukjent"}
                  </div>
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className="h-3 w-3" fill={n <= r.stars ? theme.accent : "none"} style={{ color: theme.accent }} />
                    ))}
                  </div>
                </div>
                <span className="text-xs" style={{ color: theme.text_soft }}>
                  {new Date(r.created_at).toLocaleDateString("nb-NO")}
                </span>
              </div>
              {r.text && (
                <p className="text-sm" style={{ color: theme.text_soft, lineHeight: 1.6 }}>{r.text}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BroadcastModal({ open, onClose, theme }) {
  const { t: tr, i18n } = useTranslation();
  const [status, setStatus] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  // Segment-filtre (B)
  const [only7d, setOnly7d] = useState(false);
  const [only30d, setOnly30d] = useState(false);
  const [onlyVip, setOnlyVip] = useState(false);
  const [includeCreators, setIncludeCreators] = useState(false);
  const [previewCount, setPreviewCount] = useState(null);

  React.useEffect(() => {
    if (open) {
      api.get("/me/creator-broadcast").then((r) => setStatus(r.data));
      setText("");
      setOnly7d(false); setOnly30d(false); setOnlyVip(false); setIncludeCreators(false);
      setPreviewCount(null);
    }
  }, [open]);

  // Når 7d aktiveres, deaktiver 30d (eksklusivt)
  React.useEffect(() => { if (only7d && only30d) setOnly30d(false); }, [only7d]);
  React.useEffect(() => { if (only30d && only7d) setOnly7d(false); }, [only30d]);

  // Live preview-count
  React.useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      try {
        const r = await api.post("/me/creator-broadcast/preview", {
          text: text || "preview",
          only_active_7d: only7d,
          only_active_30d: only30d,
          only_vip: onlyVip,
          include_creators: includeCreators,
        });
        setPreviewCount(r.data?.recipient_count ?? null);
      } catch { setPreviewCount(null); }
    }, 250);
    return () => clearTimeout(t);
  }, [open, only7d, only30d, onlyVip, includeCreators]);

  if (!open) return null;

  const send = async () => {
    if (text.trim().length < 2) return;
    setSending(true);
    try {
      const r = await api.post("/me/creator-broadcast", {
        text,
        only_active_7d: only7d,
        only_active_30d: only30d,
        only_vip: onlyVip,
        include_creators: includeCreators,
      });
      toast.success(tr("creator.broadcast_sent", { n: r.data.sent_to }));
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("creator.broadcast_err"));
    } finally {
      setSending(false);
    }
  };

  const blocked = status && !status.can_send;
  const nextDt = status?.next_send_at ? new Date(status.next_send_at) : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4" onClick={onClose}>
      <div className="w-full max-w-xl bg-[var(--noir-elev)] border border-white/10 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="font-fraunces italic text-2xl text-[var(--cream)]">{tr("creator.broadcast_title")}</h2>
          <button onClick={onClose} className="text-[var(--cream-soft)]">✕</button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-[var(--cream-soft)]"
             dangerouslySetInnerHTML={{ __html: tr("creator.broadcast_lead", { n: status?.follower_count ?? "…" }) }}
          />

          {/* Segment-velger (B) */}
          <div className="border border-white/10 p-3 space-y-2">
            <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)]">
              {tr("creator.broadcast_filter_title")}
            </div>
            <label className="flex items-center gap-2 text-sm text-[var(--cream-soft)] cursor-pointer">
              <input type="checkbox" checked={only7d} onChange={(e) => setOnly7d(e.target.checked)} data-testid="bc-only-7d" />
              {tr("creator.broadcast_only_7d")}
            </label>
            <label className="flex items-center gap-2 text-sm text-[var(--cream-soft)] cursor-pointer">
              <input type="checkbox" checked={only30d} onChange={(e) => setOnly30d(e.target.checked)} data-testid="bc-only-30d" />
              {tr("creator.broadcast_only_30d")}
            </label>
            <label className="flex items-center gap-2 text-sm text-[var(--cream-soft)] cursor-pointer">
              <input type="checkbox" checked={onlyVip} onChange={(e) => setOnlyVip(e.target.checked)} data-testid="bc-only-vip" />
              {tr("creator.broadcast_only_vip")}
            </label>
            <label className="flex items-center gap-2 text-sm text-[var(--cream-soft)] cursor-pointer">
              <input type="checkbox" checked={includeCreators} onChange={(e) => setIncludeCreators(e.target.checked)} data-testid="bc-include-creators" />
              {tr("creator.broadcast_include_creators")} <span className="text-[var(--cream-faint)] text-xs">{tr("creator.broadcast_include_creators_hint")}</span>
            </label>
            <div className="text-xs text-[var(--magenta)] mt-1">
              {previewCount === null ? tr("creator.broadcast_calculating") : tr("creator.broadcast_will_send_to", { n: previewCount })}
            </div>
          </div>

          {blocked && nextDt && (
            <div className="text-sm px-4 py-3 border border-[var(--magenta)]/40 text-[var(--magenta)]">
              {tr("creator.broadcast_next", { date: nextDt.toLocaleString(i18n.language === "en" ? "en-GB" : i18n.language === "sv" ? "sv-SE" : i18n.language === "da" ? "da-DK" : "nb-NO") })}
            </div>
          )}
          <textarea
            value={text} onChange={(e) => setText(e.target.value)}
            data-testid="broadcast-text"
            rows={5} maxLength={2000} disabled={blocked}
            placeholder={tr("creator.broadcast_placeholder")}
            className="w-full bg-[var(--noir)] border border-white/10 px-4 py-3 text-[var(--cream)] outline-none disabled:opacity-50"
          />
          <div className="text-xs text-[var(--cream-faint)] text-right">{text.length}/2000</div>
        </div>
        <div className="px-6 py-4 border-t border-white/10 flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2 text-sm text-[var(--cream-soft)]">{tr("creator.cancel")}</button>
          <button onClick={send} disabled={blocked || sending || text.trim().length < 2 || previewCount === 0}
            data-testid="broadcast-send-btn"
            className="px-6 py-2 bg-[var(--magenta)] text-black font-meta disabled:opacity-50">
            {sending ? tr("creator.sending") : tr("creator.broadcast_send")}
          </button>
        </div>
      </div>
    </div>
  );
}

function VertStat({ num, label, theme, small = false }) {
  const isString = typeof num === "string";
  const display = isString ? num : (num || 0).toLocaleString("nb-NO");
  return (
    <div className="px-4 py-3 border-b last:border-b-0" style={{ borderColor: theme.border }}>
      <div className="c-stat-label" style={{ margin: 0, fontSize: 10 }}>{label}</div>
      <div
        className={`tabular-nums mt-1 pl-3 ${small ? "text-lg" : "text-3xl"}`}
        style={{
          fontFamily: `'${theme.heading_font}', serif`,
          fontStyle: theme.heading_style,
          color: theme.text,
          fontWeight: 600,
          lineHeight: 1,
        }}
      >
        {display}
      </div>
    </div>
  );
}

function LinkButton({ link, creatorId }) {
  const [qrOpen, setQrOpen] = useState(false);
  const trackClick = () => {
    api.post("/creator-link-click", { link_id: link.id, creator_id: creatorId }).catch(() => {});
    // (Legacy-endepunktet speiler også til creator_element_clicks – ekstra kall ikke nødvendig.)
  };

  if (link.type === "qr") {
    const qrSrc = link.qr_image_url
      ? `${process.env.REACT_APP_BACKEND_URL}/api/creator-qr/raw?path=${encodeURIComponent(link.qr_image_url)}`
      : null;
    return (
      <>
        <button
          type="button"
          onClick={() => { trackClick(); setQrOpen(true); }}
          data-testid={`c-link-${link.platform}`}
          className="c-link-button"
        >
          <div className="flex items-center gap-3 min-w-0">
            <PlatformIcon platform={link.platform} url={link.url} size={24} />
            <div className="min-w-0">
              <div className="truncate">{link.label || link.platform_label || link.platform}</div>
              <div className="c-link-platform">{link.platform_label || link.platform} · QR</div>
            </div>
          </div>
          {qrSrc ? (
            <img src={qrSrc} alt="QR" className="h-10 w-10 object-contain bg-white p-0.5 rounded" />
          ) : (
            <QrCode className="h-4 w-4 opacity-60" />
          )}
        </button>
        {qrOpen && (
          <div
            className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-6"
            onClick={() => setQrOpen(false)}
            data-testid={`qr-modal-${link.platform}`}
          >
            <div
              className="bg-white p-6 rounded-md flex flex-col items-center max-w-sm w-full relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setQrOpen(false)}
                className="absolute top-2 right-2 text-black/60 hover:text-black"
                aria-label={T("ui.close")}
              >
                <X className="h-5 w-5" />
              </button>
              <div className="font-meta text-xs uppercase tracking-wider text-black/60 mb-3">
                {link.platform}
              </div>
              {link.label && (
                <div className="font-fraunces italic text-xl text-black mb-4 text-center">
                  {link.label}
                </div>
              )}
              {qrSrc && (
                <img src={qrSrc} alt={T("ui.qr_code")} className="w-64 h-64 object-contain" />
              )}
              <div className="font-meta text-[11px] text-black/50 mt-4 text-center">
                Skann med telefonen din
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <a
      href={link.url} target="_blank" rel="noopener noreferrer"
      onClick={trackClick}
      data-testid={`c-link-${link.platform}`}
      className="c-link-button"
    >
      <div className="flex items-center gap-3 min-w-0">
        <PlatformIcon platform={link.platform} url={link.url} size={24} />
        <div className="min-w-0">
          <div className="truncate">{link.label || link.platform_label || link.platform}</div>
          <div className="c-link-platform">{link.platform_label || link.platform}</div>
        </div>
      </div>
      <ExternalLink className="h-4 w-4 opacity-60 shrink-0" />
    </a>
  );
}

function CreatorTrophyStrip({ trophies, theme }) {
  const { t: tr } = useTranslation();
  // Tema-tilpasset trofé-stripe i banner-bunnen. Helt usynlig ramme – flyter
  // oppå banneret. Gradient nederst i banneret sørger for lesbarhet.
  const total = trophies.length;
  // Vis så mange som mulig – truncates naturlig med flex-wrap=nowrap + overflow
  const recent = trophies.slice(0, 8);

  const PLACE_NAME_TR = { 1: tr("creator.place_gold"), 2: tr("creator.place_silver"), 3: tr("creator.place_bronze") };

  return (
    <div
      className="absolute bottom-0 left-0 right-0 z-10 pb-2 pt-8 pointer-events-none hidden md:block"
      data-testid="creator-trophy-strip"
    >
      <div className="max-w-screen-2xl mx-auto px-6 pointer-events-auto">
      <div className="flex items-center gap-4 flex-wrap">
        {/* Total – lengst til venstre */}
        <div className="flex items-center gap-2 shrink-0">
          <span
            className="font-meta text-[10px] uppercase tracking-[0.25em] leading-none"
            style={{ color: theme.accent, textShadow: "0 1px 4px rgba(0,0,0,0.6)" }}
          >
            {tr("creator.on_podium")}
          </span>
          <span
            className="c-heading text-3xl leading-none"
            style={{ color: theme.text, textShadow: "0 2px 12px rgba(0,0,0,0.65)" }}
          >
            {total}
          </span>
        </div>

        {/* Liste – medalje + tittel, så mange som plassen tillater */}
        <div className="flex items-center gap-x-5 gap-y-2 flex-wrap min-w-0 flex-1">
          {recent.map((tt, i) => (
            <Link
              key={`${tt.contest_id}-${i}`}
              to={`/konkurranser/${tt.contest_id}`}
              className="inline-flex items-center gap-1.5 hover:opacity-80 transition group min-w-0"
              title={`${PLACE_NAME_TR[tt.place]} – ${tt.contest_title}`}
            >
              <MiniMedal place={tt.place} />
              <span
                className="text-xs truncate max-w-[220px]"
                style={{ color: theme.text, textShadow: "0 1px 6px rgba(0,0,0,0.7)" }}
              >
                {tt.contest_title}
              </span>
            </Link>
          ))}
          {total > recent.length && (
            <Link
              to={`/konkurranser/vinnere`}
              className="text-[10px] font-meta uppercase tracking-[0.18em] hover:underline"
              style={{ color: theme.text_soft, textShadow: "0 1px 4px rgba(0,0,0,0.6)" }}
            >
              {tr("creator.more_short", { n: total - recent.length })}
            </Link>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}

// PLACE_NAME removed — translated via tr("creator.place_*")

function MiniMedal({ place }) {
  const COLORS = { 1: "#D4A574", 2: "#C7CBD1", 3: "#B26A3F" };
  const c = COLORS[place] || COLORS[3];
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M8 1 L4 8 L8 12 L12 8 Z" fill={c} opacity="0.5" />
      <path d="M16 1 L20 8 L16 12 L12 8 Z" fill={c} opacity="0.5" />
      <circle cx="12" cy="15" r="7" fill={c} />
      <circle cx="12" cy="15" r="5" fill="rgba(0,0,0,0.25)" />
      <text
        x="12" y="18.5"
        textAnchor="middle"
        fontSize="7.5"
        fontWeight="700"
        fill="#fff"
        fontFamily="'Fraunces', serif"
        fontStyle="italic"
      >
        {place}
      </text>
    </svg>
  );
}

function TeaserCard({ t, creatorId, creatorHandle }) {
  const isVideo = t.media_type === "video";
  const src = (t.thumbnail_path || t.url || "").startsWith("http") ? (t.thumbnail_path || t.url) : (t.url || t.thumbnail_path);
  const linkTo = creatorHandle
    ? `/skaper/${creatorHandle}/galleri?id=${t.id}`
    : `/bilder?id=${t.id}`;
  return (
    <Link to={linkTo}
      onClick={() => trackElementClick(creatorId, "media", t.id)}
      className="block relative aspect-[3/4] overflow-hidden c-card" style={{padding:0}}>
      {src ? (
        <img src={src} alt={t.caption || ""} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center" style={{color:"var(--c-text-soft)"}}>
          {isVideo ? <Play className="h-8 w-8"/> : "—"}
        </div>
      )}
      {t.requires_tier && t.requires_tier !== "free" && (
        <div className="absolute inset-0 backdrop-blur-md flex items-center justify-center bg-black/30">
          <Lock className="h-6 w-6 text-white" />
        </div>
      )}
      {isVideo && (
        <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-1.5 py-0.5">VIDEO</div>
      )}
    </Link>
  );
}

export default function BetaSkaperSide() {
  return (
    <AgeGateGuard>
      <BetaSkaperSideInner />
    </AgeGateGuard>
  );
}

function BetaSkaperSideInner() {
  const { t: tr, i18n } = useTranslation();
  const { username: rawUsername } = useParams();
  const username = (rawUsername || "").replace(/^@/, "");
  const [searchParams] = useSearchParams();
  const highlightPromoId = searchParams.get("promo");
  const { user: me } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [newAlbumOpen, setNewAlbumOpen] = useState(false);
  const [broadcastOpen, setBroadcastOpen] = useState(false);

  // Åpne broadcast-modal fra ?broadcast=1 (fra Skaper-studio)
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("broadcast") === "1") setBroadcastOpen(true);
    if (p.get("edit") === "1") setEditOpen(true);
  }, []);
  const [following, setFollowing] = useState(false);
  const [showOpenRequest, setShowOpenRequest] = useState(false);
  const [insights, setInsights] = useState(null);
  const [previewMode, setPreviewMode] = useState(false); // skjul owner-UI for å forhåndsvise
  const [reviews, setReviews] = useState({ results: [], count: 0, avg_stars: 0, can_review: false, my_review: null });
  const [ads, setAds] = useState([]);
  const [promos, setPromos] = useState([]);
  const [trophies, setTrophies] = useState([]);
  const [wheels, setWheels] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [tilbud, setTilbud] = useState([]);
  const [showTour, setShowTour] = useState(false);
  const [postReloadKey, setPostReloadKey] = useState(0);

  const loadReviews = () => {
    api.get(`/users/${username}/creator-reviews`).then((r) => setReviews(r.data)).catch(() => {});
  };

  const loadAdsPromos = () => {
    api.get(`/users/${username}/creator-ads`).then((r) => setAds(r.data?.results || [])).catch(() => setAds([]));
    api.get(`/users/${username}/creator-promos`).then((r) => setPromos(r.data?.results || [])).catch(() => setPromos([]));
    api.get(`/users/${username}/wheels`).then((r) => setWheels(r.data?.results || [])).catch(() => setWheels([]));
    api.get(`/users/${username}/creator-tilbud`).then((r) => setTilbud(r.data?.items || [])).catch(() => setTilbud([]));
  };

  const loadAlbums = () => {
    // Iter 47-kontrakt: Skapersiden viser KUN creator-pool-album
    api.get(`/albums?owner=${username}&pool=creator&limit=24`)
      .then((r) => setAlbums(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAlbums([]));
  };

  const load = () => {
    setLoading(true);
    api.get(`/users/${username}/creator-page`)
      .then((r) => {
        setData(r.data);
        setFollowing(r.data.am_following);
        if (r.data.is_owner) {
          api.get("/me/creator-insights").then((ir) => setInsights(ir.data)).catch(() => {});
        }
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    loadReviews();
    loadAdsPromos();
    loadAlbums();
  };

  useEffect(() => { load(); }, [username]);

  // Vis creator-tour: første gang etter approval ELLER hvis ?tour=1 i URL
  useEffect(() => {
    if (!data?.is_owner) return;
    const params = new URLSearchParams(window.location.search);
    const force = params.get("tour") === "1";
    const firstTime = me?.is_creator && (me?.creator_tour_completed === false
      || !localStorage.getItem(`ne_creator_tour_v2_${me?.id}`));
    if (force || firstTime) {
      const t = setTimeout(() => setShowTour(true), 800);
      return () => clearTimeout(t);
    }
  }, [data?.is_owner, me?.is_creator, me?.creator_tour_completed, me?.id]);

  // Trofé-skap for creator – vises i banner-stripen + i stats-kolonnen
  useEffect(() => {
    if (!username) return;
    api.get(`/users/${username}/trophies`)
      .then((r) => setTrophies(r.data?.trophies || []))
      .catch(() => setTrophies([]));
  }, [username]);

  const t = data?.theme;
  const style = useMemo(() => (t ? themeStyle(t) : {}), [t]);

  if (loading) return <Layout withSidebar={false}><div className="text-center text-[var(--cream-soft)] py-20">{tr("creator.loading")}</div></Layout>;
  if (data?.paused) {
    return (
      <Layout withSidebar={false} noVikingPattern>
        <div className="panel-tight p-12 text-center max-w-xl mx-auto" data-testid="creator-paused">
          <div className="font-meta text-[10px] tracking-[0.3em] text-[var(--magenta)] mb-3">{tr("creator.paused")}</div>
          <h1 className="font-fraunces italic text-3xl text-[var(--cream)] mb-3">
            {tr("creator.paused_title", { name: data.username_display || tr("creator.paused_default_name") })}
          </h1>
          <p className="text-[var(--cream-soft)] text-sm leading-relaxed">
            {tr("creator.paused_desc")}
          </p>
        </div>
      </Layout>
    );
  }
  if (!data?.user) {
    return (
      <Layout withSidebar={false} noVikingPattern>
        <div className="panel-tight p-12 text-center max-w-xl mx-auto">
          <h1 className="font-fraunces italic text-3xl text-[var(--cream)] mb-2">{tr("creator.not_found_title")}</h1>
          <p className="text-[var(--cream-soft)] text-sm">{tr("creator.not_found_desc")}</p>
        </div>
      </Layout>
    );
  }

  const u = data.user;
  const counts = data.counts || {};
  const teasers = data.teasers || [];
  const links = (u.creator_links || []).slice().sort((a, b) => (a.position || 0) - (b.position || 0));
  const localeMap = { no: "nb-NO", sv: "sv-SE", da: "da-DK", en: "en-GB" };
  const memberSince = u.created_at ? new Date(u.created_at).toLocaleDateString(localeMap[i18n.language] || "nb-NO", { year: "numeric", month: "long" }) : "";

  const toggleFollow = async () => {
    if (!me) { nav("/?tab=login"); return; }
    try {
      await api.post(`/users/${u.username}/follow`);
      setFollowing((v) => !v);
    } catch { toast.error(tr("creator.follow_err")); }
  };

  const bannerSrc = u.creator_banner_url
    ? (u.creator_banner_url.startsWith("http")
        ? u.creator_banner_url
        : `${process.env.REACT_APP_BACKEND_URL}/api/creator-banners/raw?path=${encodeURIComponent(u.creator_banner_url)}`)
    : null;

  const ownerView = data.is_owner && !previewMode;

  const SectionEditBtn = ({ to }) => ownerView ? (
    to ? (
      <Link
        to={to}
        data-testid="inline-edit-link"
        className="ml-2 inline-flex items-center justify-center w-6 h-6 opacity-50 hover:opacity-100 transition"
        style={{ color: t.accent }}
        title={T("ui.edit_in_studio")}
      >
        <Pencil className="h-3.5 w-3.5" />
      </Link>
    ) : (
      <button
        onClick={() => setEditOpen(true)}
        data-testid="inline-edit-btn"
        className="ml-2 inline-flex items-center justify-center w-6 h-6 opacity-50 hover:opacity-100 transition"
        style={{ color: t.accent }}
        title={T("ui.edit")}
      >
        <Pencil className="h-3.5 w-3.5" />
      </button>
    )
  ) : null;

  return (
    <Layout withSidebar={false} noVikingPattern wide>
      <div className="creator-page-wrap" style={style} data-testid="creator-page" data-theme={t.id}>
        {/* HERO BANNER med IDENT inni – fader ut mot høyre */}
        <div className="relative" style={{ minHeight: 290, marginBottom: 0 }}>
          {bannerSrc ? (
            <img src={bannerSrc} alt="" className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0" style={{
              background: `linear-gradient(135deg, ${t.accent}33 0%, ${t.surface} 60%, ${t.bg} 100%)`,
            }} />
          )}
          {/* Fade fra venstre (mørk bak avatar/tekst) */}
          <div className="absolute inset-0" style={{
            background: `linear-gradient(to right, ${t.bg}dd 0%, ${t.bg}c0 22%, ${t.bg}77 42%, transparent 67%)`,
          }} />
          {/* Soft fade ned mot innholdet – forsterket i nederste tredjedel
              så trofé-stripen alltid har lesbar bakgrunn uavhengig av bilde */}
          <div className="absolute inset-0 pointer-events-none" style={{
            background: `linear-gradient(to bottom, transparent 50%, ${t.bg}aa 75%, ${t.bg} 100%)`,
          }} />

          {/* Eier-CTA-er — desktop (lg+). Tett på én linje øverst-høyre, god margin mot avatar. */}
          {data.is_owner && !previewMode && (
            <div className="hidden lg:absolute lg:-top-11 lg:right-8 lg:flex lg:flex-nowrap gap-1 z-20 items-center">
              <Link
                to="/skaper/studio"
                data-testid="creator-studio-btn"
                data-tour="studio"
                className="c-btn c-btn-outline text-[10px] px-2 py-1 gap-1 shrink-0"
                style={{ background: "rgba(0,0,0,0.5)" }}
              >
                <Briefcase className="h-3 w-3" /> {tr("creator.studio_btn")}
              </Link>
              <button
                onClick={() => setBroadcastOpen(true)}
                data-testid="creator-broadcast-btn"
                data-tour="broadcast"
                className="c-btn c-btn-outline text-[10px] px-2 py-1 gap-1 shrink-0"
                style={{ background: "rgba(0,0,0,0.5)" }}
              >
                <Send className="h-3 w-3" /> {tr("creator.send_followers")}
              </button>
              <Link
                to="/meldinger?context=creator"
                data-testid="creator-inbox-btn"
                className="c-btn c-btn-outline text-[10px] px-2 py-1 gap-1 shrink-0"
                style={{ background: "rgba(0,0,0,0.5)" }}
              >
                <MessageCircle className="h-3 w-3" /> {tr("creator.inbox_btn", "Skaper-innboks")}
              </Link>
              <button
                onClick={() => setEditOpen(true)}
                data-testid="creator-edit-btn"
                data-tour="edit"
                className="c-btn c-btn-outline text-[10px] px-2 py-1 gap-1 shrink-0"
                style={{ background: "rgba(0,0,0,0.5)" }}
              >
                <Pencil className="h-3 w-3" /> {tr("creator.edit_page")}
              </button>
              <button
                onClick={() => setPreviewMode(true)}
                data-testid="creator-preview-btn"
                data-tour="preview"
                className="c-btn c-btn-outline text-[10px] px-2 py-1 gap-1 shrink-0"
                style={{ background: "rgba(0,0,0,0.5)" }}
              >
                <EyeOff className="h-3 w-3" /> {tr("creator.preview")}
              </button>
              <Link
                to="/innstillinger"
                data-testid="creator-settings-btn"
                className="c-btn c-btn-outline text-[10px] px-2 py-1 gap-1 shrink-0"
                style={{ background: "rgba(0,0,0,0.5)" }}
              >
                <Sliders className="h-3 w-3" /> {tr("creator.settings_btn", "Innstillinger")}
              </Link>
              <button
                onClick={() => setShowTour(true)}
                data-testid="creator-show-tour-btn"
                aria-label={tr("tour_creator.show_again")}
                className="c-btn c-btn-outline text-[10px] px-2 py-1 gap-1 shrink-0"
                style={{ background: "rgba(0,0,0,0.5)" }}
                title={tr("tour_creator.show_again")}
              >
                <HelpCircle className="h-3 w-3" /> {tr("creator.tour_btn", "Omvisning")}
              </button>
            </div>
          )}
          {data.is_owner && previewMode && (
            <button
              onClick={() => setPreviewMode(false)}
              data-testid="creator-exit-preview-btn"
              className="absolute top-4 right-4 z-20 c-btn c-btn-solid"
            >
              <Eye className="h-4 w-4" /> {tr("creator.exit_preview")}
            </button>
          )}

          {/* IDENT INNI banneret – avatar venstre topp, navn UNDER bildet på desktop, ved siden av på mobil */}
          <div className="relative z-10 max-w-screen-2xl mx-auto px-4 sm:px-6 pt-4 sm:pt-6 pb-6 sm:pb-8 flex flex-col md:flex-row md:items-start md:justify-between gap-4 md:gap-6">
            <div className="flex flex-row md:flex-col items-start gap-3 md:gap-3 min-w-0">
              <div
                data-tour="avatar"
                className="w-24 h-24 md:w-48 md:h-48 shrink-0 border-2 overflow-hidden"
                style={{ borderColor: t.accent, background: t.surface_2 }}
              >
                {u.avatar_url ? (
                  <img src={u.avatar_url} alt={u.username} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center c-heading text-2xl md:text-4xl">
                    {u.username.slice(0, 2).toUpperCase()}
                  </div>
                )}
              </div>
              <div data-tour="handle" className="min-w-0 max-w-full flex-1" style={{ textShadow: "0 2px 12px rgba(0,0,0,0.55), 0 1px 3px rgba(0,0,0,0.7)" }}>
                <h1 className={`c-heading flex items-center flex-wrap break-words ${
                  (u.username || "").length > 16
                    ? "text-xl md:text-3xl"
                    : (u.username || "").length > 12
                    ? "text-2xl md:text-4xl"
                    : "text-3xl md:text-5xl"
                }`}>
                  <span className="break-words">{u.username}</span>
                  {u.is_verified && <span className="c-verified" title={T("ui.verified")}>✓</span>}
                </h1>
                {data.is_owner && (
                  <div className="mt-1"><AgeVerifyBadge isSelf={true} /></div>
                )}
                {u.creator_tagline && <p className="c-tagline mt-1 text-sm md:text-lg">{u.creator_tagline}</p>}
                {(u.city || u.fylke || u.country) && (
                  <p className="text-[10px] md:text-xs mt-1 md:mt-2 inline-flex items-center gap-1.5" style={{ color: t.text_soft, letterSpacing: "0.2em", textTransform: "uppercase" }}>
                    {u.country && <CountryFlag iso={u.country} size="sm" data-testid="skaperside-flag" />}
                    <span>{[u.city, u.fylke].filter(Boolean).join(" · ")}</span>
                  </p>
                )}
              </div>
            </div>

            {!data.is_owner && (
              <div className="flex gap-2 flex-wrap">
                <button onClick={toggleFollow} data-testid="creator-follow-btn"
                  className={`c-btn ${following ? "c-btn-outline" : "c-btn-solid"}`}>
                  <Heart className="h-4 w-4" />
                  {following ? tr("creator.following") : tr("creator.follow")}
                </button>
                {me && u.allow_creator_dm && (
                  <Link to={`/meldinger?til=${u.username}&via=creator`} data-testid="creator-send-dm-btn" className="c-btn c-btn-outline">
                    <MessageCircle className="h-4 w-4" /> {tr("profile.send_message")}
                  </Link>
                )}
                {me && me.id !== u.id && (
                  <Link to={`/glimt/send/${u.username}`} data-testid="creator-send-glimt-btn" className="c-btn c-btn-outline">
                    <Zap className="h-4 w-4" /> {T("ui.send_glimt")}
                  </Link>
                )}
                {me?.is_creator && me.id !== u.id && (
                  <button onClick={() => setShowOpenRequest(true)} data-testid="creator-open-request-btn" className="c-btn c-btn-outline">
                    <Handshake className="h-4 w-4" /> {T("open_req.button")}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* TROFÉ-STRIPE – full bredde nederst i banneret (kun desktop; mobil får egen stripe under) */}
          {trophies.length > 0 && (
            <CreatorTrophyStrip trophies={trophies} theme={t} />
          )}
        </div>

        {/* MOBIL TROFÉ-STRIPE – under banneret, unngår overlap med tekst */}
        {trophies.length > 0 && (
          <div className="md:hidden px-4 py-3 border-b" style={{ borderColor: 'rgba(255,255,255,0.08)', background: 'rgba(0,0,0,0.35)' }} data-testid="creator-trophy-strip-mobile">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="font-meta text-[9px] uppercase tracking-[0.25em]" style={{ color: t.accent }}>
                {trophies.length} pallplasseringer
              </span>
            </div>
            <div className="flex items-center gap-3 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
              {trophies.slice(0, 8).map((tt, i) => (
                <Link
                  key={`${tt.contest_id}-m-${i}`}
                  to={`/konkurranser/${tt.contest_id}`}
                  className="inline-flex items-center gap-1 shrink-0"
                  style={{ color: t.text }}
                >
                  <MiniMedal place={tt.place} />
                  <span className="text-[11px] truncate max-w-[140px]">{tt.contest_title}</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* IDENT er nå inni banneret – gammel duplisert blokk fjernet */}

        {/* MOBIL: Eier-CTA-toolbar under banneret (3-kolonne × 2 rader). SEND TIL FØLGERE er skilt ut. */}
        {data.is_owner && !previewMode && (
          <div className="lg:hidden px-4 py-3 border-b" style={{ borderColor: 'rgba(255,255,255,0.08)' }} data-testid="creator-mobile-toolbar">
            <div className="grid grid-cols-3 gap-2">
              <Link
                to="/skaper/studio"
                data-testid="mobile-creator-studio-btn"
                className="c-btn c-btn-outline text-[10px] py-2 flex flex-col items-center justify-center gap-1 leading-tight text-center"
                style={{ background: "rgba(0,0,0,0.4)" }}
              >
                <Briefcase className="h-3.5 w-3.5" />
                <span>{tr("creator.studio_btn")}</span>
              </Link>
              <button
                onClick={() => setBroadcastOpen(true)}
                data-testid="mobile-creator-broadcast-btn"
                className="c-btn c-btn-outline text-[10px] py-2 flex flex-col items-center justify-center gap-1 leading-tight text-center"
                style={{ background: "rgba(0,0,0,0.4)" }}
              >
                <Send className="h-3.5 w-3.5" />
                <span>{tr("creator.send_followers")}</span>
              </button>
              <Link
                to="/meldinger?context=creator"
                data-testid="mobile-creator-inbox-btn"
                className="c-btn c-btn-outline text-[10px] py-2 flex flex-col items-center justify-center gap-1 leading-tight text-center"
                style={{ background: "rgba(0,0,0,0.4)" }}
              >
                <MessageCircle className="h-3.5 w-3.5" />
                <span>{tr("creator.inbox_btn", "Skaper-innboks")}</span>
              </Link>
              <button
                onClick={() => setEditOpen(true)}
                data-testid="mobile-creator-edit-btn"
                className="c-btn c-btn-outline text-[10px] py-2 flex flex-col items-center justify-center gap-1 leading-tight text-center"
                style={{ background: "rgba(0,0,0,0.4)" }}
              >
                <Pencil className="h-3.5 w-3.5" />
                <span>{tr("creator.edit_page")}</span>
              </button>
              <button
                onClick={() => setPreviewMode(true)}
                data-testid="mobile-creator-preview-btn"
                className="c-btn c-btn-outline text-[10px] py-2 flex flex-col items-center justify-center gap-1 leading-tight text-center"
                style={{ background: "rgba(0,0,0,0.4)" }}
              >
                <EyeOff className="h-3.5 w-3.5" />
                <span>{tr("creator.preview")}</span>
              </button>
              <Link
                to="/innstillinger"
                data-testid="mobile-creator-settings-btn"
                className="c-btn c-btn-outline text-[10px] py-2 flex flex-col items-center justify-center gap-1 leading-tight text-center"
                style={{ background: "rgba(0,0,0,0.4)" }}
              >
                <Sliders className="h-3.5 w-3.5" />
                <span>{tr("creator.settings_btn", "Innstillinger")}</span>
              </Link>
              <button
                onClick={() => setShowTour(true)}
                data-testid="mobile-creator-show-tour-btn"
                className="c-btn c-btn-outline text-[10px] py-2 flex flex-col items-center justify-center gap-1 leading-tight text-center"
                style={{ background: "rgba(0,0,0,0.4)" }}
              >
                <HelpCircle className="h-3.5 w-3.5" />
                <span>{tr("tour_creator.show_again")}</span>
              </button>
            </div>
          </div>
        )}

        {/* 3-KOLONNE: LENKER | TEKST | STATS. Mobil: om-tekst full bredde, deretter lenker+stats parallelt */}
        <div className="max-w-screen-2xl mx-auto mt-2 px-4 sm:px-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4 sm:gap-6 lg:gap-8">
          {/* GRØNN SONE – LENKER + ANMELDELSER (venstre) */}
          <aside className="sm:col-span-1 lg:col-span-3 order-2 lg:order-1">
            <div data-testid="creator-links" data-tour="links">
              <div className="c-section-title flex items-center">{tr("creator.find_me_here")} <SectionEditBtn to="/skaper/studio?tab=links" /></div>
              {links.length === 0 ? (
                <div className="c-card text-center text-sm" style={{ color: t.text_soft }}>
                  {ownerView
                    ? tr("creator.links_empty_owner")
                    : tr("creator.no_links_added")}
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {links.map((l) => <LinkButton key={l.id} link={l} creatorId={u.id} />)}
                </div>
              )}
            </div>

            {/* LYKKEHJUL – synlig på siden hvis skaper har aktive hjul */}
            {wheels.length > 0 && (
              <div className="mt-6" data-testid="creator-wheels">
                <div className="c-section-title flex items-center gap-2">
                  <Sparkles className="h-3.5 w-3.5" /> {tr("creator.wheel")}
                </div>
                <div className="flex flex-col gap-2">
                  {wheels.map((w) => (
                    <Link
                      key={w.id}
                      to={`/lykkehjul/${w.slug}`}
                      data-testid={`wheel-${w.id}`}
                      className="c-card flex items-center gap-3 hover:opacity-90 transition"
                      style={{ borderColor: t.accent }}
                    >
                      <div className="shrink-0" style={{ pointerEvents: "none" }}>
                        <SpinWheel segments={w.segments} size={64} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{w.title}</div>
                        <div className="font-meta text-[10px] opacity-70">
                          {tr("creator.wheel_segments_count", { n: w.segments?.length || 0 })}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* ANMELDELSER – kompakt under lenker */}
            <CompactReviewsSection
              username={u.username}
              theme={t}
              reviews={reviews}
              me={me}
              isOwner={data.is_owner}
              onChanged={loadReviews}
            />

            {/* AKTIVE ANNONSER – flyttet hit til venstre kolonne (Iter 47.3) */}
            {(ads.length > 0 || ownerView) && (
              <div className="mt-6" data-testid="creator-ads">
                <div className="c-section-title">{tr("creator.active_ads")}</div>
                {ads.length === 0 ? (
                  <div className="c-card text-center text-sm" style={{ color: t.text_soft }}>
                    {tr("creator.no_active_ads")}
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    {ads.slice(0, 6).map((ad) => <AdCard key={ad.id} ad={ad} theme={t} creatorId={u.id} />)}
                  </div>
                )}
                {ads.length > 6 && (
                  <Link to={`/annonser?owner=${u.username}`} className="block text-center text-xs mt-3 underline" style={{ color: t.accent }}>
                    {tr("creator.see_all_n", { n: ads.length })}
                  </Link>
                )}
              </div>
            )}
          </aside>

          {/* GUL SONE – OM-TEKST + PROMOER (midten, bredere) */}
          <section className="sm:col-span-2 lg:col-span-7 order-1 lg:order-2">
            <div className="c-section-title flex items-center">{tr("creator.about_prefix")} {u.username} <SectionEditBtn /></div>
            {(u.creator_about || u.creator_bio) ? (
              <div className="c-card" style={{ whiteSpace: "pre-wrap", lineHeight: 1.75, fontSize: 16 }}>
                {u.creator_about || u.creator_bio}
              </div>
            ) : (
              <div className="c-card text-center text-sm" style={{ color: t.text_soft }}>
                {ownerView ? tr("creator.about_empty_owner") : tr("creator.no_presentation_yet")}
              </div>
            )}

            {/* WALL-FEED: PostComposer (kun eier) + PostFeedList */}
            <div className="mt-8" data-testid="beta-skaper-wall">
              {ownerView && (
                <PostComposer pool="creator" onPosted={() => setPostReloadKey((k) => k + 1)} />
              )}
              <PostFeedList userId={u.id} pool="creator" reloadKey={postReloadKey} />
            </div>
          </section>

          {/* RØD SONE – TILBUD & PROMO + STATS (høyre, smal, stacked) */}
          <aside className="sm:col-span-1 lg:col-span-2 order-3" data-testid="creator-stats">
            {/* ALBUM-CARD (flyttet fra midten) – lenker til galleri + ny-knapp for eier */}
            <div className="mb-6" data-testid="beta-skaper-album-card">
              <div className="c-section-title flex items-center justify-between">
                <span>{tr("creator.albums", "Album")}</span>
                {data.is_owner && !previewMode && (
                  <button
                    type="button"
                    onClick={() => setNewAlbumOpen(true)}
                    data-tour="new-album"
                    data-testid="beta-skaper-album-new-btn"
                    className="c-btn c-btn-outline text-[10px] inline-flex items-center gap-1 shrink-0 px-2 py-1"
                    style={{ background: "rgba(0,0,0,0.4)", borderColor: t.accent, color: t.accent }}
                  >
                    <Plus className="h-3 w-3" /> {tr("creator.new_album", "Nytt")}
                  </button>
                )}
              </div>
              <Link
                to={`/skaper/${u.username}/galleri`}
                data-testid="beta-skaper-album-link"
                className="c-card flex items-center gap-3 hover:opacity-90 transition"
                style={{ padding: 12 }}
              >
                <ImageIcon className="h-5 w-5 shrink-0" style={{ color: t.accent }} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate" style={{ color: t.text }}>
                    {albums.length > 0
                      ? tr("creator.albums_count", { n: albums.length, defaultValue: "{{n}} album" }).replace("{{n}}", albums.length)
                      : (data.is_owner && !previewMode
                          ? tr("creator.albums_empty", "Du har ingen skaper-album enda.")
                          : tr("creator.no_albums", "Ingen album"))}
                  </div>
                  <div className="font-meta text-[10px] uppercase tracking-wider" style={{ color: t.text_soft }}>
                    {tr("profile.see_all", "Se alle")}
                  </div>
                </div>
              </Link>
            </div>

            {/* NYE TILBUD (Iter 58) – fra Skaper-studio */}
            {tilbud.length > 0 && (
              <div className="mb-6" data-testid="creator-tilbud">
                <div className="c-section-title">{tr("creator.tilbud_title")}</div>
                <div className="flex flex-col gap-2">
                  {tilbud.map((p) => (
                    <div key={p.id} className="c-card" data-testid={`tilbud-card-${p.id}`}>
                      <div className="font-fraunces italic text-base" style={{ color: t.text }}>
                        {p.title}
                      </div>
                      {p.body && (
                        <div className="text-xs mt-1 whitespace-pre-line" style={{ color: t.text_soft }}>{p.body}</div>
                      )}
                      {p.qr_url && (
                        <a href={p.qr_url} target="_blank" rel="noopener noreferrer"
                          className="mt-3 block bg-white p-2 w-32 mx-auto" title="QR">
                          <img src={p.qr_url} alt="QR" className="w-full h-full object-contain" />
                        </a>
                      )}
                      {p.link_url && (
                        <a href={p.link_url} target="_blank" rel="noopener noreferrer"
                          className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-meta tracking-[0.15em] uppercase"
                          style={{ background: t.accent, color: t.bg }}>
                          <ExternalLink className="h-3 w-3" /> {p.link_label || tr("creator.tilbud_see_offer")}
                        </a>
                      )}
                      {p.expires_at && (
                        <div className="font-meta text-[10px] mt-2" style={{ color: t.text_soft, opacity: 0.7 }}>
                          {tr("creator.tilbud_expires_at", { date: new Date(p.expires_at).toLocaleDateString(i18n.language === "en" ? "en-GB" : i18n.language === "sv" ? "sv-SE" : i18n.language === "da" ? "da-DK" : "nb-NO") })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* TILBUD & PROMO – flyttet hit over stats (Iter 47.3) */}
            {(promos.length > 0 || ownerView) && (
              <div className="mb-6" data-testid="creator-promos">
                <div className="c-section-title flex items-center">{tr("creator.promos_title")} <SectionEditBtn to="/skaper/studio?tab=promos" /></div>
                {promos.length === 0 ? (
                  <div className="c-card text-center text-sm" style={{ color: t.text_soft }}>
                    {ownerView ? tr("creator.promos_empty_owner") : tr("creator.promos_empty")}
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    {promos.map((p) => <PromoCard key={p.id} promo={p} theme={t} creatorId={u.id} highlight={highlightPromoId === p.id} />)}
                  </div>
                )}
              </div>
            )}

            <div className="c-section-title">{tr("creator.statistics")}</div>
            <div className="c-card flex flex-col" style={{ padding: 0 }}>
              <VertStat num={u.follower_count} label={tr("creator.followers")} theme={t} />
              <VertStat num={counts.images} label={tr("creator.images")} theme={t} />
              <VertStat num={counts.videos} label={tr("creator.videos")} theme={t} />
              <VertStat num={counts.noveller} label={tr("creator.stories")} theme={t} />
              {trophies.length > 0 && (
                <VertStat num={trophies.length} label={tr("creator.won_label")} theme={t} />
              )}
              {counts.rating_count > 0 && (
                <VertStat num={`★ ${counts.avg_rating.toFixed(1)}`} label={tr("creator.rating_count", { n: counts.rating_count })} theme={t} />
              )}
              {memberSince && (
                <VertStat num={memberSince} label={tr("creator.member_since")} theme={t} small />
              )}
            </div>

            {/* MIN INNSIKT – kompakt sammendrag for eier, lenker til full side (skjult på mobil per bruker-krav 18.07.2026) */}
            {ownerView && insights && (
              <div className="mt-6 hidden lg:block" data-testid="creator-insights">
                <div className="c-section-title flex items-center gap-2">
                  <TrendingUp className="h-3.5 w-3.5" /> {tr("creator.my_insights")}
                </div>
                <div className="c-card flex flex-col" style={{ padding: 0 }}>
                  <VertStat num={insights.views_30d}         label={tr("creator.views_30d")} theme={t} />
                  <VertStat num={insights.clicks_30d}        label={tr("creator.clicks_30d")} theme={t} />
                  <VertStat num={insights.new_followers_30d} label={tr("creator.new_followers_30d")} theme={t} />
                </div>
                <Link
                  to="/skaper/innsikt"
                  data-testid="link-to-min-innsikt"
                  className="mt-3 block text-center text-xs font-meta tracking-[0.18em] py-2 border transition hover:opacity-80"
                  style={{ color: t.accent, borderColor: t.accent }}
                >
                  {tr("creator.open_full_insights")}
                </Link>
              </div>
            )}

            {/* AKTIVE ANNONSER er flyttet til venstre kolonne (Iter 47.3) */}
          </aside>
        </div>

        {/* Promoer er flyttet inn under Om (midten), annonser inn under Min innsikt (høyre). Ingen separat rad. */}

        {/* Bilder/Video og Anmeldelser er nå inni 3-kolonne-griden */}

        {data.is_owner && (
          <BroadcastModal open={broadcastOpen} onClose={() => setBroadcastOpen(false)} theme={t} />
        )}

        {/* OM-seksjon fjernet – Om-tekst vises nå i 3-kol-layouten over */}

        {data.is_owner && (
          <CreatorEditModal
            open={editOpen} onClose={() => setEditOpen(false)} initial={u}
            onSaved={() => { setEditOpen(false); load(); }}
          />
        )}
        {data.is_owner && (
          <CreatorAlbumModal
            open={newAlbumOpen}
            onClose={() => setNewAlbumOpen(false)}
            onCreated={() => { loadAlbums(); }}
          />
        )}
        {showOpenRequest && (
          <OpenRequestModal targetUsername={u.username} targetUserId={u.id}
            onClose={() => setShowOpenRequest(false)}
            onSent={() => setShowOpenRequest(false)} />
        )}
        {data.is_owner && showTour && (
          <CreatorTour
            onClose={() => { setShowTour(false); localStorage.setItem(`ne_creator_tour_v2_${me?.id}`, "1"); }}
            completeEndpoint="/me/creator-tour-completed"
            steps={CREATOR_TOUR_STEPS.map((s) => ({
              ...s,
              title: tr(s.titleKey),
              body: tr(s.bodyKey),
            }))}
          />
        )}
      </div>
    </Layout>
  );
}
